import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import * as Scrivito from "scrivito";
import { QuoteSliderPanel } from "./QuoteSliderPanel";

function QuoteSliderWidget({ widget }) {
  const slides = widget.get("quotes");

  const trackRef = useRef(null); // reference to container containg all slides
  const slideRef = useRef(null); // reference to the first slide (used to calculate the width)
  const [currentIndex, setCurrentIndex] = useState(0); // active slide
  const [slideWidth, setSlideWidth] = useState(0); // width + margin of one slide for correct animation
  const intervalRef = useRef(null); // refernce to the interval used by auto slide
  const interval = 7000; // interval for the auto slide

  // recalulate the width of a slide
  const handleResize = () => {
    if (slideRef.current) {
      const width = slideRef.current.offsetWidth + 30; // 30px margin
      setSlideWidth(width);
    }
  };

  // stop the auto slide e.g. on mouse over
  const clearAutoSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // start the interval for auto slide all 7 seconds
  const startAutoSlide = useCallback(() => {
    if (!Scrivito.isInPlaceEditingActive() && slides.length > 1) {
      clearAutoSlide();
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex >= slides.length - 1 ? 0 : prevIndex + 1
        );
      }, interval);
    }
  }, [slides.length]);

  // onetime: recalculate width of a slide on resize
  useLayoutEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // set the tranform for the slide effect
  useEffect(() => {
    handleResize();
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(-${
        currentIndex * slideWidth
      }px)`;
    }
  }, [currentIndex, slideWidth]);

  // start autoslide
  useEffect(() => {
    startAutoSlide();
    return clearAutoSlide;
  }, [startAutoSlide]);

  // set slide index used by indicator, resets auto slide timer
  const setSlide = (slide) => {
    setCurrentIndex(slide);
    startAutoSlide();
  };

  return (
    <div
      className="quote-slider-container position-relative pb-3"
      onMouseEnter={clearAutoSlide}
      onMouseLeave={startAutoSlide}
    >
      <div className="quote-slide-wrapper">
        <div className="quote-slider-track d-flex" ref={trackRef}>
          {slides.map((quote, index) => (
            <div
              className={`quote-slide ${
                index === currentIndex ? "--active" : "--inactive"
              }`}
              key={index}
              ref={index === 0 ? slideRef : null}
            >
              <QuoteSliderPanel quote={quote} />
            </div>
          ))}
          {slides.length > 1 && (
            <div className="quote-slide">
              <QuoteSliderPanel quote={slides[0]} />
            </div>
          )}
        </div>
      </div>
      <div className="slider-controls">
        {slides.map((_quote, index) => (
          <button
            key={index}
            onClick={() => setSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={`${index === currentIndex ? "--active" : "--inactive"}`}
          />
        ))}
      </div>
    </div>
  );
}

Scrivito.provideComponent(
  "QuoteSliderWidget",
  Scrivito.connect(QuoteSliderWidget)
);
